import {ChangeEvent, FC, Suspense, useCallback, useEffect, useRef, useState} from "react";
import * as fabric from 'fabric';
import BaseModal from "@memaro/components/Modal.tsx";
import apiClient from "../../../lib/api.ts";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/SaveAlt";
import FilterIcon from "@mui/icons-material/Filter";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Canvas, {CanvasHandle} from "./Canvas.tsx";
import IconButton from "@mui/material/IconButton";
import {useFeedback} from "@memaro/feedback";

export type ModalProps = {
    profileID: string;
    open: boolean;
    onClose: () => void;
}

const loadQRCode = async (profileID: string): Promise<fabric.Image> => {
    const qrCodeURL = await apiClient.getQRCodeURL(profileID)
    return fabric.FabricImage.fromURL(qrCodeURL, {crossOrigin: "anonymous"})
}

const Modal: FC<ModalProps> = ({profileID, open, onClose}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<CanvasHandle>(null)
    const [qrCodeImage, setQRCodeImage] = useState<fabric.Image | null>(null);
    const [mainImage, setMainImage] = useState<fabric.Image | null>(null);
    const {error} = useFeedback()

    const handleClose = useCallback(() => {
        setQRCodeImage(null);
        setMainImage(null);
    }, [])

    useEffect(() => {
        if (!open) {
            handleClose()
        } else {
            loadQRCode(profileID)
                .then(setQRCodeImage)
                .catch(() => error?.("Er is een fout opgetreden bij ophalen van de QR-code"))
            return handleClose
        }
    }, [open, profileID, handleClose, error]);

    const handleClick = useCallback(() => {
        inputRef.current?.click();
    }, [])

    const handleInputChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length == 1) {
            fabric.FabricImage.fromURL(URL.createObjectURL(event.target.files[0]), {crossOrigin: "anonymous"})
                .then(setMainImage)
                .catch(() => error?.("Er is een fout opgetreden bij het laden van de foto."))
        }
    }, [error])

    const handleSave = useCallback(() => {
        canvasRef.current?.save()
    }, [])

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            title="Foto met QR-code maken"
            fullScreen
            actions={[
                <Button key="add" onClick={handleSave} disabled={!mainImage}>
                    <SaveIcon/>
                    Opslaan
                </Button>,
                <Button key="close" onClick={onClose}>
                    Sluiten
                </Button>
            ]}
        >
            <Stack justifyContent="center" alignItems="center" height="100%">
                {!qrCodeImage ? (
                    <CircularProgress/>
                ) : !mainImage ? (
                    <IconButton onClick={handleClick} sx={{padding: 3}}>
                        <FilterIcon sx={{fontSize: 50}} />
                    </IconButton>
                ) : (
                    <Suspense fallback={<CircularProgress />}>
                        <Canvas
                            ref={canvasRef}
                            qrCodeImage={qrCodeImage}
                            mainImage={mainImage}
                        />
                    </Suspense>
                )}
            </Stack>
            <input ref={inputRef} type="file" onChange={handleInputChange} style={{display: 'none'}}/>
        </BaseModal>
    )
}

export default Modal
